import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogHeader } from '@you/ui'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import {
  workspaceAppService,
  type WorkspaceLicenseItem,
  type WorkspaceCodeItem,
} from '@/api/services/workspaceApp.service'
import sketchToArtImg from '@/assets/images/agent/sketch-to-art.png'
import poetryMasterImg from '@/assets/images/agent/poetry-master.png'
import idiomStrategistImg from '@/assets/images/agent/idiom-strategist.png'
import timeDialogueImg from '@/assets/images/agent/time-dialogue.png'
import soundForgeImg from '@/assets/images/agent/sound-forge.png'
import mathHunterImg from '@/assets/images/agent/math-hunter.png'
import englishBlastImg from '@/assets/images/agent/english-blast.png'
import timeDreamerImg from '@/assets/images/agent/time-dreamer.png'

// 胶囊型认证 Tag（非侧边栏场景使用）
const OrgTag = () => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border"
    style={{ background: '#FFF7E0', borderColor: '#F5A623', color: '#D48806' }}
  >
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7.5" fill="#FFF7E0" stroke="#F5A623" />
      <path d="M4.5 8.5L7 11L11.5 5.5" stroke="#F5A623" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    认证组织
  </span>
)

const FLAVOR_COLORS: Record<string, string> = {
  aiPainter:       '#6043FF',
  sketchToArt:     '#13C2C2',
  poetryMaster:    '#722ED1',
  idiomStrategist: '#EB2F96',
  timeDialogue:    '#FA8C16',
  soundForge:      '#52C41A',
  mathHunter:      '#1677FF',
  englishBlast:    '#F5222D',
  timeDreamer:     '#FF7A45',
}

const FLAVOR_ICONS: Record<string, string> = {
  sketchToArt:     sketchToArtImg,
  poetryMaster:    poetryMasterImg,
  idiomStrategist: idiomStrategistImg,
  timeDialogue:    timeDialogueImg,
  soundForge:      soundForgeImg,
  mathHunter:      mathHunterImg,
  englishBlast:    englishBlastImg,
  timeDreamer:     timeDreamerImg,
}

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).catch(() => {
    const el = document.createElement('textarea')
    el.value = text
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
  })
}

const Apps = () => {
  const { currentWorkspace } = useWorkspaceStore()
  const workspaceId = Number(currentWorkspace?.id)

  const [licenses, setLicenses] = useState<WorkspaceLicenseItem[]>([])
  const [loadingLicenses, setLoadingLicenses] = useState(false)
  const [selectedFlavor, setSelectedFlavor] = useState<WorkspaceLicenseItem | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const [codes, setCodes] = useState<WorkspaceCodeItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loadingCodes, setLoadingCodes] = useState(false)
  const [copiedId, setCopiedId] = useState<number | null>(null)

  useEffect(() => {
    if (!workspaceId) return
    setLoadingLicenses(true)
    workspaceAppService.getLicenses(workspaceId)
      .then(setLicenses)
      .catch(console.error)
      .finally(() => setLoadingLicenses(false))
  }, [workspaceId])

  const loadCodes = (flavor: WorkspaceLicenseItem, p = 1) => {
    setSelectedFlavor(flavor)
    setPage(p)
    setLoadingCodes(true)
    workspaceAppService.getCodes(workspaceId, flavor.flavorId, p)
      .then(res => { setCodes(res.items); setTotal(res.total) })
      .catch(console.error)
      .finally(() => setLoadingCodes(false))
  }

  const handleAppClick = (item: WorkspaceLicenseItem) => {
    setModalOpen(true)
    loadCodes(item, 1)
  }

  const handleCopy = (code: WorkspaceCodeItem) => {
    copyToClipboard(code.code)
    setCopiedId(code.id)
    setTimeout(() => setCopiedId(null), 1500)
  }

  const expiresText = (ts: number | null) => {
    if (!ts) return <span className="text-blue-500 text-xs">永久</span>
    const soon = ts - Date.now() < 30 * 86400000
    const d = new Date(ts)
    const fmt = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return <span className={`text-xs ${soon ? 'text-red-500' : 'text-gray-500'}`}>{fmt}</span>
  }

  const totalPages = Math.ceil(total / 20)

  return (
    <div className="space-y-6">
      <h3 className="text-base font-semibold text-gray-700">应用授权</h3>
      {loadingLicenses ? (
        <div className="text-sm text-gray-400">加载中…</div>
      ) : licenses.length === 0 ? (
        <div className="text-sm text-gray-400">暂无授权应用</div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {licenses.map(item => {
            const color = FLAVOR_COLORS[item.flavorId] ?? '#6043FF'
            const usedPct = item.ableTokens + item.usedTokens > 0
              ? Math.round(item.usedTokens / (item.ableTokens + item.usedTokens) * 100)
              : 0
            return (
              <div
                key={item.flavorId}
                onClick={() => handleAppClick(item)}
                className="flex items-center justify-between rounded-xl border border-gray-100 px-4 py-3 cursor-pointer transition-all hover:border-[#8752FF50] hover:bg-[#8752FF04]"
              >
                <div className="flex items-center gap-3">
                  {FLAVOR_ICONS[item.flavorId] ? (
                    <img
                      src={FLAVOR_ICONS[item.flavorId]}
                      alt={item.flavorDisplayName}
                      className="w-8 h-8 rounded-lg object-cover flex-none"
                    />
                  ) : (
                    <span className="inline-block w-8 h-8 rounded-lg flex-none" style={{ background: color }} />
                  )}
                  <span className="font-medium text-sm">{item.flavorDisplayName}</span>
                </div>
                <div className="flex items-center gap-6 text-xs text-gray-500">
                  <span>已激活 <b className="text-gray-800">{item.activatedCount}</b> / {item.maxDevices} 台</span>
                  <span>
                    可用 <b className="text-green-600">{item.ableTokens.toLocaleString()}</b>
                    <span className="mx-1 text-gray-300">|</span>
                    已用 <b className="text-gray-800">{item.usedTokens.toLocaleString()}</b>
                    {usedPct > 0 && <span className="ml-1 text-gray-400">({usedPct}%)</span>}
                  </span>
                  <span>到期：{expiresText(item.expiresAt)}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* 激活码弹窗 */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader
            title={selectedFlavor ? `「${selectedFlavor.flavorDisplayName}」激活码` : '激活码'}
          />
          <div className="flex-1 overflow-auto px-1">
            {loadingCodes ? (
              <div className="text-sm text-gray-400 py-8 text-center">加载中…</div>
            ) : codes.length === 0 ? (
              <div className="text-sm text-gray-400 py-8 text-center">暂无激活码</div>
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-3">共 {total} 个</p>
                <div className="rounded-xl border border-gray-100 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium">激活码</th>
                        <th className="text-left px-4 py-2.5 font-medium">状态</th>
                        <th className="text-left px-4 py-2.5 font-medium">绑定设备</th>
                        <th className="text-left px-4 py-2.5 font-medium">使用时间</th>
                        <th className="text-left px-4 py-2.5 font-medium">生成时间</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {codes.map(code => (
                        <tr key={code.id} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs">{code.code}</span>
                              <button
                                type="button"
                                onClick={e => { e.stopPropagation(); handleCopy(code) }}
                                className="flex-none text-gray-400 hover:text-[#8752FF] transition-colors"
                                title="复制激活码"
                              >
                                {copiedId === code.id ? (
                                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8.5L6.5 12L13 5" stroke="#52C41A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                ) : (
                                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                    <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                                    <path d="M3 11V3.5A1.5 1.5 0 0 1 4.5 2H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            {code.status === 1 ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-600">已使用</span>
                            ) : (
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">未使用</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-gray-500">
                            {code.usedAndroidId ? `${code.usedAndroidId.slice(0, 8)}…` : '-'}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-500">{code.usedAt ?? '-'}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-500">{code.createTime}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {total > 20 && (
                  <div className="flex items-center justify-end gap-2 mt-3 text-sm text-gray-500">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => selectedFlavor && loadCodes(selectedFlavor, page - 1)}
                      className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                    >
                      上一页
                    </button>
                    <span>{page} / {totalPages}</span>
                    <button
                      type="button"
                      disabled={page >= totalPages}
                      onClick={() => selectedFlavor && loadCodes(selectedFlavor, page + 1)}
                      className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                    >
                      下一页
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export { OrgTag }
export default Apps
